import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import FreebieInline from "@/components/FreebieInline";
import SectionLabel from "@/components/SectionLabel";
import Crown from "@/components/Crown";
import PhotoPlaceholder from "@/components/PhotoPlaceholder";
import ASSETS from "@/lib/assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MARQUEE_PHRASES = [
  "FREE DOWNLOAD",
  "IDENTITY RESET",
  "5 PROMPTS",
  "GRIT™ FRAMEWORK",
  "RETURN TO YOURSELF",
  "HIGH-PERFORMING WOMEN",
];

const FreebieMarquee = () => {
  const items = [...MARQUEE_PHRASES, ...MARQUEE_PHRASES, ...MARQUEE_PHRASES, ...MARQUEE_PHRASES];
  return (
    <div className="marquee" data-testid="freebie-marquee">
      <div className="marquee-track">
        {items.map((p, i) => (
          <React.Fragment key={i}>
            <span>{p}</span>
            <span className="sep">✦</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const Freebie = () => {
  // Inline newsletter mini-form on the freebie page
  const [nlEmail, setNlEmail] = useState("");
  const [nlDone, setNlDone] = useState(false);
  const [nlLoading, setNlLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!nlEmail.trim()) return;
    setNlLoading(true);
    try {
      await axios.post(`${API}/leads/newsletter`, { email: nlEmail.trim() });
      setNlDone(true);
      toast.success("Subscribed. Check your inbox soon.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setNlLoading(false);
    }
  };

  return (
    <div data-testid="page-freebie">
      {/* HERO with form/card */}
      <section style={{ background: "var(--gold)", padding: "120px 0 80px" }}>
        <div className="container-x" style={{ maxWidth: 980, marginBottom: 30 }}>
          <SectionLabel center>FREE DOWNLOAD &nbsp;·&nbsp; FROM NIJAH ALMOND</SectionLabel>
        </div>
        <div className="container-x">
          <FreebieInline variant="bare" />
        </div>
      </section>

      <FreebieMarquee />

      {/* FROM NIJAH section with pink-blazer photo */}
      <section className="section-pad bg-cream" data-testid="from-nijah">
        <div className="container-x">
          <div className="split">
            <div style={{ minHeight: 460 }}>
              <PhotoPlaceholder src={ASSETS.photoPinkBlazer} position="center 25%" label="Nijah Almond" />
            </div>
            <div>
              <SectionLabel>FROM NIJAH</SectionLabel>
              <h2 className="section-title text-teal">
                The Mirror Method is for the woman <span className="italic-c">who’s done waiting.</span>
              </h2>
              <p className="body-base">
                I created The Mirror Method because I know what it feels like to look at your life
                and think: <em>“This looks right on paper. So why does it feel so wrong?”</em>
              </p>
              <p className="body-base">
                This isn’t another productivity download. It’s exactly what the name says — a mirror.
                A structured way to reconnect with the woman who was there before the titles, the
                achievements, and the expectations.
              </p>
              <p className="body-base">
                You don’t have to figure this out alone. Start here.
              </p>
              <Link
                to="/contact"
                className="btn btn-teal"
                style={{ marginTop: 22, alignSelf: "flex-start" }}
                data-testid="freebie-apply"
              >
                Apply to Work With Nijah →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER inline */}
      <section className="section-pad bg-blush" data-testid="freebie-newsletter">
        <div className="container-x" style={{ maxWidth: 760, textAlign: "center" }}>
          <Crown size={32} opacity={0.85} />
          <SectionLabel center>ALSO JOIN THE NEWSLETTER</SectionLabel>
          <h2 className="section-title text-teal" style={{ marginTop: 12 }}>
            Weekly real talk, <span className="italic-c">delivered to you.</span>
          </h2>
          <p className="body-base" style={{ maxWidth: 560, margin: "0 auto 24px" }}>
            Identity insights, leadership strategies, and behind-the-scenes access — every week. No fluff.
          </p>
          {nlDone ? (
            <p style={{ fontSize: 16, color: "var(--teal)", fontWeight: 500 }} data-testid="freebie-nl-success">
              ✓ You’re in. Welcome to the room.
            </p>
          ) : (
            <form
              onSubmit={subscribe}
              className="form-grid-2"
              style={{ maxWidth: 520, margin: "0 auto", gridTemplateColumns: "1fr auto" }}
              data-testid="freebie-newsletter-form"
            >
              <input
                className="input-base"
                type="email"
                placeholder="Your email address"
                value={nlEmail}
                onChange={(e) => setNlEmail(e.target.value)}
                required
                data-testid="freebie-nl-email"
              />
              <button className="btn btn-teal" type="submit" disabled={nlLoading} data-testid="freebie-nl-submit">
                {nlLoading ? "…" : "Subscribe"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* GRIT LAUNCH WAITLIST */}
      <section
        className="section-pad bg-teal"
        style={{ textAlign: "center" }}
        data-testid="grit-waitlist-section"
      >
        <div className="container-x" style={{ maxWidth: 780 }}>
          <Crown size={36} opacity={0.95} />
          <div
            className="eyebrow gold"
            style={{ justifyContent: "center", marginTop: 16 }}
          >
            COMING SOON · BY APPLICATION
          </div>
          <h2
            className="section-title"
            style={{ color: "#fff", marginTop: 12 }}
          >
            Join the <span className="italic-c text-gold">GRIT™ Launch Waitlist.</span>
          </h2>
          <p
            className="body-base"
            style={{ color: "rgba(253,248,245,.85)", maxWidth: 600, margin: "0 auto 26px" }}
          >
            The Mirror Method shows you the truth. GRIT™ is the 12-week system that
            rebuilds you from the inside out. Get early access, founding-member pricing,
            and first dibs on the next cohort.
          </p>
          <a
            href="https://gritlaunchwaitlist.nijahalmond.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
            data-testid="grit-waitlist-link"
          >
            Join the GRIT™ Waitlist →
          </a>
        </div>
      </section>

      {/* READY TO GO DEEPER CTA */}
      <section className="cta-band" data-testid="freebie-cta-deeper">
        <div className="cta-inner">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Crown size={42} opacity={0.95} />
          </div>
          <div className="eyebrow gold" style={{ justifyContent: "center", marginTop: 16 }}>
            READY TO GO DEEPER?
          </div>
          <h2 className="h-display">
            The Mirror Method is the first step.
            <br />
            <span className="italic-c text-gold">Coaching is the journey.</span>
          </h2>
          <p className="cta-sub">
            When you’re ready to go beyond the prompts — Nijah is here.
          </p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary" data-testid="freebie-deep-apply">
              Apply Now →
            </Link>
            <Link to="/programs" className="btn btn-ghost" data-testid="freebie-deep-explore">
              Explore Programs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Freebie;
